import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/api'
import type { AssetLibraryItem, AssetLibraryStats, JobOutput } from '@/api/types'
import { PagedList } from '@/components/PagedList'
import { TaskCard } from '@/components/TaskCard'
import { Icon } from '@/components/icons'
import { Badge, Button, Card, CardBody, CardHead, Empty, Input, Stat } from '@/components/ui'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useToast } from '@/context/ToastContext'
import { rememberFallback } from '@/lib/assetFallback'
import {
  categoryById,
  FALLBACK_CATEGORIES,
  FALLBACK_ORIGINS,
  type AssetCategoryId,
} from '@/config/assetTaxonomy'
import type { Job } from '@/context/JobCenterContext'
// ⚠️ 归组规则**不在本文件**：`pages/AiModels.tsx` 用的是同一份
// （那边一组 = 一个模特，这边一组 = 一个任务图集 —— 组长什么样各写各的，
//  「哪几张算一组」必须只有一份实现）。见 `lib/assetGroups.ts` 头部。
import { groupByTask, type AssetGroup } from '@/lib/assetGroups'

type OriginFilter = 'all' | 'generated' | 'upload'
type KindFilter = 'all' | 'image' | 'video' | 'text'

/** 归组结果 → 任务卡认识的数据形状。
 *
 * 任务卡吃的是 `Job`（任务中心的行），作品图集没有状态机，所以这里
 * 造一条**恒为已完成、不带耗时与消耗**的伪 Job —— 任务卡里那些行
 * 会因为没有值而自动不渲染（见 TaskCard 的详情块）。
 */
function groupToJob(g: AssetGroup): Job {
  const first = g.items[0]
  const outputs: JobOutput[] = g.items.map((it) => ({
    url: it.url,
    thumbUrl: it.thumbUrl,
    type: it.kind,
    text: it.contentText,
  }))
  const ts = Date.parse(first.createdAt) || Date.now()
  return {
    id: `album-${g.key}`,
    serverJobId: g.jobId || undefined,
    title: g.title,
    appSlug: first.appSlug || first.sourcePage,
    sourcePage: first.sourcePage,
    model: first.sourceModel,
    modelName: first.sourceModel || undefined,
    prompt: first.prompt ?? '',
    inputs: {},
    imageUrls: [],
    // 空串 = 未知比例，任务卡会跳过「规格」这一行；网格退回默认方形
    aspectRatio: '',
    expectedOutputs: g.items.length,
    estimatedMs: 0,
    state: 'succeeded',
    progress: 100,
    outputs,
    createdAt: ts,
    finishedAt: ts,
    // 刻意**不设 remote**：那个标记是「任务中心从后端读回的历史记录」，
    // 用于和本机会话任务区分。作品图集根本不进任务队列，打上它只会在卡片上
    // 多出一个「历史」标签，纯噪音。
  }
}

/* -------------------------------------------------------------------------- */

export function MyWorks() {
  const { addGenerated } = useAssetLibrary()
  const toast = useToast()

  const [category, setCategory] = useState<string>('all')
  const [origin, setOrigin] = useState<OriginFilter>('all')
  const [kind, setKind] = useState<KindFilter>('all')
  const [rawQuery, setRawQuery] = useState('')
  const [query, setQuery] = useState('')

  const [items, setItems] = useState<AssetLibraryItem[]>([])
  const [stats, setStats] = useState<AssetLibraryStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState<string | null>(null)

  // 搜索防抖：每敲一个字就打一次接口会把这个列表打成筛子
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(rawQuery.trim()), 300)
    return () => window.clearTimeout(t)
  }, [rawQuery])

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true)
      try {
        const [res, st] = await Promise.all([
          api.listAssetLibrary({ category, origin, kind, q: query, perPage: 300 }),
          api.assetLibraryStats(),
        ])
        setItems(res.items)
        // 登记「图床地址 → 本地副本」兜底：图床图被删/被墙时 SmartImage 自动换本地副本
        res.items.forEach((it) => rememberFallback(it.thumbUrl || it.url, it.localUrl))
        setStats(st)
        setFailed(null)
      } catch (e) {
        setFailed(e instanceof Error ? e.message : '资产库加载失败')
      } finally {
        setLoading(false)
      }
    },
    [category, origin, kind, query],
  )

  useEffect(() => {
    void load()
  }, [load])

  const groups = useMemo(() => groupByTask(items), [items])

  /* ------------------------------ 交互 ------------------------------ */

  /** 改分类：**整组一起改**。
   * 同一个任务的 N 张图本来就是同一用途（自动推导也是按任务推的），
   * 一次只改一张只会让一组里出现两种分类。
   */
  const changeCategory = useCallback(
    async (group: AssetGroup, next: string) => {
      const prev = items
      const ids = new Set(group.items.map((i) => i.id))
      setItems((list) => list.map((it) => (ids.has(it.id) ? { ...it, assetCategory: next } : it)))
      try {
        await Promise.all(group.items.map((it) => api.updateAssetItem(it.id, { assetCategory: next })))
        toast.success(`已改为「${categoryById(next).label}」· ${group.items.length} 张`)
        void load({ silent: true })
      } catch (e) {
        setItems(prev)
        toast.error(e instanceof Error ? e.message : '改分类失败')
      }
    },
    [items, load, toast],
  )

  const toggleFavoriteGroup = useCallback(
    async (group: AssetGroup) => {
      const next = !group.items[0].favorite
      const ids = new Set(group.items.map((i) => i.id))
      setItems((list) => list.map((it) => (ids.has(it.id) ? { ...it, favorite: next } : it)))
      try {
        await Promise.all(group.items.map((it) => api.updateAssetItem(it.id, { favorite: next })))
      } catch {
        setItems((list) => list.map((it) => (ids.has(it.id) ? { ...it, favorite: !next } : it)))
        toast.error('收藏失败')
      }
    },
    [items, toast],
  )

  /** 删除：单张与整组走同一个出口，区别只是传几个 id */
  const removeItems = useCallback(
    async (ids: string[], what: string) => {
      if (!ids.length) return
      if (!confirm(`删除${what}？此操作不可撤销。`)) return
      const prev = items
      const drop = new Set(ids)
      setItems((list) => list.filter((it) => !drop.has(it.id)))
      try {
        await Promise.all(ids.map((id) => api.deleteAssetItem(id)))
        toast.success('已删除')
        void load({ silent: true })
      } catch (e) {
        setItems(prev)
        toast.error(e instanceof Error ? e.message : '删除失败')
      }
    },
    [items, load, toast],
  )

  const toAssetLibrary = useCallback(
    (group: AssetGroup) => {
      group.items.forEach((item, i) => {
        addGenerated({
          id: `asset-${item.id}-${i}`,
          url: item.url,
          thumbUrl: item.thumbUrl,
          name: item.title,
          kind: item.kind,
          createdAt: new Date().toISOString(),
          source: 'generated',
          assetCategory: item.assetCategory,
        })
      })
      toast.success(`已存入参考图素材库 ${group.items.length} 张，可在上传区直接选用`)
    },
    [addGenerated, toast],
  )

  /* ------------------------------ 统计 ------------------------------ */

  const visibleCategories = useMemo(
    () =>
      (stats?.categories ?? [])
        .map((c) => ({ ...c, meta: categoryById(c.category) }))
        .sort((a, b) => a.meta.order - b.meta.order)
        .filter((c) => c.count > 0 || c.category === category),
    [stats, category],
  )

  const totalCount = stats?.total ?? 0
  const albumCount = groups.filter((g) => g.jobId).length

  const exportManifest = () => {
    const header = 'created\tgroup\ttype\tcategory\torigin\tkind\ttitle\tmodel\turl'
    const lines = groups.flatMap((g) =>
      g.items.map((it) =>
        [
          it.createdAt,
          g.jobId || '（未归组）',
          g.key,
          categoryById(it.assetCategory).label,
          it.origin === 'upload' ? '我上传的' : 'AI生成',
          it.kind,
          it.title,
          it.sourceModel,
          it.url,
        ].join('\t'),
      ),
    )
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/plain;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `asset-manifest-${Date.now()}.txt`
    a.click()
    toast.success(`已导出 ${items.length} 条清单（${groups.length} 组）`)
  }

  const activeCategoryMeta = category === 'all' ? null : categoryById(category)

  return (
    <div className="page page--wide">
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <Stat label="全部资产" value={totalCount} icon="folder" />
        <Stat label="AI 生成" value={stats?.works ?? 0} icon="sparkles" />
        <Stat label="我上传的" value={stats?.assets ?? 0} icon="upload" />
        <Stat label="当前筛选" value={items.length} icon="search" />
      </div>

      <Card>
        <CardHead
          title="我的作品"
          icon="folder"
          right={
            <>
              <Input
                value={rawQuery}
                onChange={(e) => setRawQuery(e.target.value)}
                placeholder="搜索标题或提示词"
                style={{ width: 180 }}
              />
              <Button
                variant="outline"
                size="sm"
                icon="download"
                onClick={exportManifest}
                disabled={!items.length}
              >
                导出清单
              </Button>
            </>
          }
        />
        <CardBody>
          {/* 一级：资产类别（这是什么图）—— 数量从接口来，空分类不渲染 */}
          <div className="asset-filter">
            <span className="asset-filter__label">用途</span>
            <div className="asset-filter__chips">
              <button
                type="button"
                className="chip"
                data-active={category === 'all'}
                onClick={() => setCategory('all')}
              >
                全部 {totalCount}
              </button>
              {visibleCategories.map((c) => (
                <button
                  key={c.category}
                  type="button"
                  className="chip"
                  data-active={category === c.category}
                  onClick={() => setCategory(c.category)}
                  title={c.meta.desc}
                >
                  {c.meta.label} {c.count}
                </button>
              ))}
            </div>
          </div>

          {/* 二级：来源（从哪来）—— 与用途正交，两者可叠加 */}
          <div className="asset-filter">
            <span className="asset-filter__label">来源</span>
            <div className="asset-filter__chips">
              <button
                type="button"
                className="chip"
                data-active={origin === 'all'}
                onClick={() => setOrigin('all')}
              >
                全部
              </button>
              {FALLBACK_ORIGINS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="chip"
                  data-active={origin === o.id}
                  onClick={() => setOrigin(o.id)}
                  title={o.desc}
                >
                  {o.label}
                </button>
              ))}
              <span className="asset-filter__sep" />
              {(
                [
                  ['all', '不限格式'],
                  ['image', '图片'],
                  ['video', '视频'],
                  ['text', '文案'],
                ] as [KindFilter, string][]
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  className="chip"
                  data-active={kind === k}
                  onClick={() => setKind(k)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="asset-toolbar">
            <span className="text-xs muted">
              {loading
                ? '加载中…'
                : `共 ${items.length} 件 · 归为 ${groups.length} 组${albumCount ? `（其中任务图集 ${albumCount} 个）` : ''}`}
              {activeCategoryMeta ? ` · ${activeCategoryMeta.label}：${activeCategoryMeta.desc}` : ''}
            </span>
            {(category !== 'all' || origin !== 'all' || kind !== 'all' || query) && (
              <Button
                variant="ghost"
                size="sm"
                icon="x"
                onClick={() => {
                  setCategory('all')
                  setOrigin('all')
                  setKind('all')
                  setRawQuery('')
                }}
              >
                重置筛选
              </Button>
            )}
          </div>

          {failed ? (
            <Empty
              icon="alert"
              title="资产库加载失败"
              text={`${failed}。确认后端已启动（默认 127.0.0.1:8000），或改用离线模式。`}
            />
          ) : !items.length ? (
            <Empty
              icon="folder"
              title={loading ? '正在加载…' : totalCount ? '没有匹配的资产' : '作品库是空的'}
              text={
                totalCount
                  ? '换个类别、来源或关键词试试。'
                  : '在任意工作台生成一次，这一次的产出会自动归成一个任务图集。'
              }
            />
          ) : (
            <PagedList
              items={groups}
              className="job-list"
              pageSize={10}
              pageSizes={[10, 20, 50]}
              resetKey={`${category}|${origin}|${kind}|${query}`}
              unit="组"
              render={(g) => (
                <AssetGroupCard
                  key={g.key}
                  group={g}
                  onCategory={(next) => changeCategory(g, next)}
                  onFavorite={() => toggleFavoriteGroup(g)}
                  onRemoveOne={(item) => removeItems([item.id], '这一张')}
                  onRemoveGroup={() => removeItems(g.items.map((i) => i.id), `这一组 ${g.items.length} 张`)}
                  onToAsset={() => toAssetLibrary(g)}
                />
              )}
            />
          )}
        </CardBody>
      </Card>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* 任务图集卡片                                                                */
/* -------------------------------------------------------------------------- */

function AssetGroupCard({
  group,
  onCategory,
  onFavorite,
  onRemoveOne,
  onRemoveGroup,
  onToAsset,
}: {
  group: AssetGroup
  onCategory: (next: string) => void
  onFavorite: () => void
  onRemoveOne: (item: AssetLibraryItem) => void
  onRemoveGroup: () => void
  onToAsset: () => void
}) {
  const job = useMemo(() => groupToJob(group), [group])
  const first = group.items[0]
  const uploaded = first.origin === 'upload'
  /** url → 资产条目：单张操作拿到的只有 url，靠它反查要删哪一条 */
  const byUrl = useMemo(() => {
    const m = new Map<string, AssetLibraryItem>()
    group.items.forEach((it) => m.set(it.url, it))
    return m
  }, [group])

  const cat = categoryById(first.assetCategory)

  return (
    <TaskCard
      job={job}
      aspectRatio=""
      /* 和右栏「本页历史」、任务中心同一个交互：整条点击右侧展开。
         这一页同样是**回看**场景（找三天前那批图），就地展开会把图集挤在一列里。 */
      drawerOnClick
      itemActions={(o) =>
        uploaded ? null : (
          <Button
            size="sm"
            variant="ghost"
            icon="trash"
            aria-label="删除这一张"
            title="删除这一张"
            onClick={() => {
              const it = byUrl.get(o.url)
              if (it) onRemoveOne(it)
            }}
          />
        )
      }
      headerExtra={
        <>
          {/* 类别直接就是个 select —— 自动推导不可能 100% 准，给用户纠正的口子。
              改的是一整组：同一任务的产出用途本就相同。 */}
          <span className="asset-cat" title={`这一组的用途（${group.items.length} 张一起改）`}>
            <Icon name={cat.icon as never} size={12} />
            <select
              value={first.assetCategory}
              onChange={(e) => onCategory(e.target.value)}
              aria-label="修改资产分类"
            >
              {FALLBACK_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </span>
          {!uploaded ? (
            <button
              className="asset-fav"
              data-active={first.favorite}
              onClick={onFavorite}
              aria-label="收藏这一组"
              title="收藏这一组"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill={first.favorite ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path d="m12 3.5 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 10l6.1-.9L12 3.5Z" />
              </svg>
            </button>
          ) : null}
          {uploaded ? <Badge tone="neutral">我上传的</Badge> : <Badge tone="primary">任务图集</Badge>}
        </>
      }
      actions={
        <>
          {!uploaded ? (
            <Button size="sm" variant="outline" icon="archive" onClick={onToAsset}>
              整组存素材
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            icon="trash"
            onClick={onRemoveGroup}
            title={uploaded ? '删除这张素材' : '删除这一组（含全部产出文件）'}
          >
            {uploaded ? '删除' : '删除整组'}
          </Button>
        </>
      }
      defaultOpen={false}
    />
  )
}

/* 型别导出：让调用方能拿到分类 id 的联合类型 */
export type { AssetCategoryId }
